import {
    Box, IconButton, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, CircularProgress, Grid
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import GroupIcon from '@mui/icons-material/Group'
import { useNavigate } from 'react-router-dom'
import {
    ApiGetIncompleteOnboardingUsers,
    ApiDeleteIncompleteOnboardingUser,
} from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';


interface IncompleteUser {
    _id: string
    firstName: string | null
    lastName: string | null
    email: string | null
    userName: string | null
    createdAt: string
    lastLoggedin: string
}

interface Pagination {
    total: number
    page: number
    limit: number
    totalPages: number
}

const DEFAULT_PAGINATION: Pagination = { total: 0, page: 1, limit: 10, totalPages: 1 }

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const IncompleteOnboardingUsers: React.FC = () => {
    const navigate = useNavigate()
    const [users, setUsers] = useState<IncompleteUser[]>([])
    const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [loading, setLoading] = useState(false)
    const [deletingId, setDeletingId] = useState<string | null>(null)

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const [pendingDeleteUser, setPendingDeleteUser] = useState<IncompleteUser | null>(null)
    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
    const [successPopup, setSuccessPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

    const fetchUsers = useCallback(async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams()
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (debouncedSearch) params.append('search', debouncedSearch)

            const res = await ApiGetIncompleteOnboardingUsers(`?${params.toString()}`)

            // response has no top-level `status` flag — data/pagination are the signal
            if (res?.data) {
                setUsers(Array.isArray(res.data) ? res.data : [])
                setPagination(res.pagination || DEFAULT_PAGINATION)
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to fetch users',
            })
        } finally {
            setLoading(false)
        }
    }, [page, rowsPerPage, debouncedSearch])

    useEffect(() => {
        fetchUsers()
    }, [fetchUsers])

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500)
        return () => clearTimeout(timer)
    }, [search])

    const handleDelete = async (userId: string) => {
        setDeletingId(userId)
        try {
            const res = await ApiDeleteIncompleteOnboardingUser(userId)
            setSuccessPopup({ open: true, message: res?.message || 'User deleted successfully' })
            setUsers(prev => prev.filter(u => u._id !== userId))
            setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }))
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to delete user',
            })
        } finally {
            setDeletingId(null)
        }
    }

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-2 align-items-center">
                    <IconButton sx={{ ':hover': { backgroundColor: '#ffffff1A' } }} onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: "#ffffff" }} />
          </IconButton>
                    <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>Incomplete Onboarding</Typography>
                </Box>
            </Box>

            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard label="Incomplete Users" value={String(pagination.total)}
                        sub="Onboarding not completed" color="#ef4444"
                        icon={<PersonOffIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard label="Users (Page)" value={String(users.length)}
                        sub={`of ${pagination.total} total`} color="#a78bfa"
                        icon={<GroupIcon fontSize="small" />} />
                </Grid>
            </Grid>

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <TextField
                        placeholder="Search by name or email"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                        size="small"
                        sx={searchFieldStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className='color-primary' />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'Name', 'Email', 'Username', 'Signed Up', 'Last Login', 'Action'].map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <CircularProgress size={24} sx={{ color: '#f2674a' }} />
                                    </TableCell>
                                </TableRow>
                            ) : users.length > 0 ? users.map((u, i) => (
                                <TableRow key={u._id} sx={trHover}>
                                    <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>
                                    <TableCell
                                        sx={{ ...tdStyle, cursor: 'pointer' }}
                                        onClick={() => navigate('/admin/user-activity', {
                                            state: { userId: u._id, userName: `${u.firstName ?? ''} ${u.lastName ?? ''}` }
                                        })}
                                    >
                                        <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                            {u.firstName || u.lastName ? `${u.firstName ?? ''} ${u.lastName ?? ''}` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>{u.email || '—'}</TableCell>
                                    <TableCell sx={tdStyle}>{u.userName || '—'}</TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{formatDate(u.createdAt)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{formatTime(u.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {u.lastLoggedin ? (
                                            <>
                                                <Typography fontSize={13} color="#e0e0e0">{formatDate(u.lastLoggedin)}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{formatTime(u.lastLoggedin)}</Typography>
                                            </>
                                        ) : <Typography fontSize={11} color="#3a3a3a" fontStyle="italic">—</Typography>}
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Tooltip title="Delete user" arrow>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={deletingId === u._id}
                                                    onClick={() => { setPendingDeleteUser(u); setConfirmDeleteOpen(true) }}
                                                    sx={{
                                                        color: '#ef4444', border: '1px solid #ef444440',
                                                        background: '#ef444410', borderRadius: '7px',
                                                        '&:hover': { background: '#ef444420' },
                                                    }}
                                                >
                                                    {deletingId === u._id
                                                        ? <CircularProgress size={14} sx={{ color: '#ef4444' }} />
                                                        : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <PersonOffIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No incomplete onboarding users found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={() => {
                    setConfirmDeleteOpen(false)
                    if (pendingDeleteUser) handleDelete(pendingDeleteUser._id)
                }}
                loading={!!deletingId}
                variant="error"
                title="Confirm Deletion"
                message="You are about to permanently delete this user. This action cannot be undone."
                confirmText="Confirm Delete"
                details={[
                    { label: 'Name', value: pendingDeleteUser ? `${pendingDeleteUser.firstName ?? ''} ${pendingDeleteUser.lastName ?? ''}` : '—' },
                    { label: 'Email', value: pendingDeleteUser?.email ?? '—' },
                ]}
                notice="This user has not completed onboarding — deleting is irreversible."
            />

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />

            <PopupDialog
                open={successPopup.open}
                onClose={() => setSuccessPopup({ open: false, message: '' })}
                onConfirm={() => setSuccessPopup({ open: false, message: '' })}
                variant="success"
                title="Success"
                message={successPopup.message}
                confirmText="Done"
                cancelText="Close"
            />
        </div>
    )
}

export default IncompleteOnboardingUsers

const thStyle = {
    backgroundColor: '#241815',
    color: '#ff6a3d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.5,
    borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
}

const tdStyle = {
    color: '#c0c0c0',
    py: 1.4,
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
}

const trHover = {
    transition: 'background 0.15s',
    '&:hover': { background: '#ff6a3d08' },
}

const searchFieldStyle = {
    width: 280,
    background: 'transparent',
    borderRadius: '7px',
    border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}